import { ChangeEvent, useContext, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { Button, FormHelperText, Grid } from '@mui/material'
import { Form, Formik, FormikErrors, FormikProps, FormikState } from 'formik'
import { useSnackbar } from 'notistack'
import * as Yup from 'yup'

import FormikTextField from '../../components/formik-input'
import IntroContainer from '../../components/intro-container'
import SubmitContainer from '../../components/submit-container'
import { AuthenticationContext } from '../../providers/auth-provider'
import Api from '../../utils/api'
import useSharedStyles from '../../utils/shared.styles'

import obfuscate from './helpers'
import useStyles from './index.styles'

interface ILoginFormValues {
  passcode: string
  passcode1: string
  passcode2: string
  passcode3: string
  passcode4: string
  passcode5: string
  passcode6: string
}

const initialValues = {
  passcode: '',
  passcode1: '',
  passcode2: '',
  passcode3: '',
  passcode4: '',
  passcode5: '',
  passcode6: '',
}

const EnterPasscode = () => {
  const classes = useStyles
  const sharedClasses = useSharedStyles

  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { state } = useLocation()

  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isResending, setIsResending] = useState<boolean>(false)

  const passcodeRef1 = useRef<HTMLInputElement>(null)
  const passcodeRef2 = useRef<HTMLInputElement>(null)
  const passcodeRef3 = useRef<HTMLInputElement>(null)
  const passcodeRef4 = useRef<HTMLInputElement>(null)
  const passcodeRef5 = useRef<HTMLInputElement>(null)
  const passcodeRef6 = useRef<HTMLInputElement>(null)

  const { login, setIsAuthenticated } = useContext(AuthenticationContext)

  useEffect(() => {
    if (!login) navigate('/login', { replace: true })
  }, [login, navigate])

  const schema = Yup.object().shape({
    passcode: Yup.string().length(6, 'Please try entering your code again').required('Please try entering your code again'),
    passcode1: Yup.string(),
    passcode2: Yup.string(),
    passcode3: Yup.string(),
    passcode4: Yup.string(),
    passcode5: Yup.string(),
    passcode6: Yup.string(),
  })

  const joinPasscode = (passcode1: string, passcode2: string, passcode3: string, passcode4: string, passcode5: string, passcode6: string) => {
    return `${passcode1}${passcode2}${passcode3}${passcode4}${passcode5}${passcode6}`
  }

  const onPaste = (
    event: ClipboardEvent | ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: string, shouldValidate?: boolean) => Promise<void | FormikErrors<ILoginFormValues>>
  ) => {
    event.preventDefault()
    const pasteData = (event as ClipboardEvent).clipboardData?.getData('Text') || (event as ChangeEvent<HTMLInputElement>).target.value || ''

    if (pasteData.length === 6) {
      setFieldValue('passcode1', pasteData[0])
      setFieldValue('passcode2', pasteData[1])
      setFieldValue('passcode3', pasteData[2])
      setFieldValue('passcode4', pasteData[3])
      setFieldValue('passcode5', pasteData[4])
      setFieldValue('passcode6', pasteData[5])
      setFieldValue('passcode', pasteData)
    }
  }

  const onChange = (
    event: ChangeEvent<HTMLInputElement>,
    nextRef: React.RefObject<HTMLInputElement | null> | undefined,
    field: string,
    values: ILoginFormValues,
    setFieldValue: (field: string, value: string, shouldValidate?: boolean) => Promise<void | FormikErrors<ILoginFormValues>>
  ) => {
    let newValue = event.target.value

    // Exactly 6 characters, treat the input as a paste (Android Chrome behaves this way); setTimeout is required for Android Chrome issues
    if (event.target.value.length === 6) setTimeout(() => onPaste(event, setFieldValue), 0)
    // More than 1 character but not 6, restrict the input to the first character
    if (event.target.value.length > 1 && event.target.value.length !== 6) {
      ;[newValue] = event.target.value
      setTimeout(() => setFieldValue(field, event.target.value[0]), 0)
    }

    let passcode = ''
    if (field === 'passcode1') passcode = joinPasscode(newValue, values.passcode2, values.passcode3, values.passcode4, values.passcode5, values.passcode6)
    if (field === 'passcode2') passcode = joinPasscode(values.passcode1, newValue, values.passcode3, values.passcode4, values.passcode5, values.passcode6)
    if (field === 'passcode3') passcode = joinPasscode(values.passcode1, values.passcode2, newValue, values.passcode4, values.passcode5, values.passcode6)
    if (field === 'passcode4') passcode = joinPasscode(values.passcode1, values.passcode2, values.passcode3, newValue, values.passcode5, values.passcode6)
    if (field === 'passcode5') passcode = joinPasscode(values.passcode1, values.passcode2, values.passcode3, values.passcode4, newValue, values.passcode6)
    if (field === 'passcode6') passcode = joinPasscode(values.passcode1, values.passcode2, values.passcode3, values.passcode4, values.passcode5, newValue)
    setFieldValue('passcode', passcode)
    nextRef?.current?.focus()
  }

  const handleSubmit = async (values: ILoginFormValues) => {
    const { passcode } = values
    const res = await Api.post('/api/v2/onetimelogin', { login, code: passcode })
    if (res.ok) {
      setIsAuthenticated(true)
    } else setSubmitError('Please try entering your code again')
  }

  const resendCode = async (resetForm: (nextState?: Partial<FormikState<ILoginFormValues>> | undefined) => void) => {
    setIsResending(true)
    const res = await Api.post('/api/v2/onetimerequest', { login })
    if (res.ok) {
      resetForm()
      setSubmitError(null)
    } else enqueueSnackbar('An error occurred.', { variant: 'error' })
    setIsResending(false)
  }

  return (
    <Grid container={true}>
      <Grid container={true} size={12} sx={classes.topSection}>
        <IntroContainer
          title="Confirm your email address"
          subtitle={
            <>
              We sent you an email to <strong>{obfuscate(login)}</strong>
            </>
          }
        />

        <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
          {({ dirty, isValid, submitCount, isSubmitting, values, setFieldValue, errors, resetForm }: FormikProps<ILoginFormValues>) => (
            <Form>
              <Grid container={true}>
                <Grid container={true} size={12} sx={classes.passcodeFieldsRow}>
                  <FormikTextField
                    name="passcode1"
                    hideErrors={true}
                    className="fs-exclude"
                    sx={classes.passcodeInput}
                    autoFocus={true}
                    value={values.passcode1}
                    onPaste={(event) => onPaste(event as unknown as ClipboardEvent, setFieldValue)}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event, passcodeRef2, 'passcode1', values, setFieldValue)}
                    slotProps={{
                      htmlInput: {
                        'aria-label': 'code field 1',
                        inputMode: 'numeric',
                      },
                    }}
                    inputRef={passcodeRef1}
                  />

                  <FormikTextField
                    name="passcode2"
                    hideErrors={true}
                    className="fs-exclude"
                    sx={classes.passcodeInput}
                    value={values.passcode2}
                    onPaste={(event) => onPaste(event as unknown as ClipboardEvent, setFieldValue)}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event, passcodeRef3, 'passcode2', values, setFieldValue)}
                    slotProps={{
                      htmlInput: {
                        'aria-label': 'code field 2',
                        inputMode: 'numeric',
                      },
                    }}
                    inputRef={passcodeRef2}
                  />

                  <FormikTextField
                    name="passcode3"
                    hideErrors={true}
                    className="fs-exclude"
                    sx={classes.passcodeInput}
                    value={values.passcode3}
                    onPaste={(event) => onPaste(event as unknown as ClipboardEvent, setFieldValue)}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event, passcodeRef4, 'passcode3', values, setFieldValue)}
                    slotProps={{
                      htmlInput: {
                        'aria-label': 'code field 3',
                        inputMode: 'numeric',
                      },
                    }}
                    inputRef={passcodeRef3}
                  />

                  <FormikTextField
                    name="passcode4"
                    hideErrors={true}
                    className="fs-exclude"
                    sx={classes.passcodeInput}
                    value={values.passcode4}
                    onPaste={(event) => onPaste(event as unknown as ClipboardEvent, setFieldValue)}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event, passcodeRef5, 'passcode4', values, setFieldValue)}
                    slotProps={{
                      htmlInput: {
                        'aria-label': 'code field 4',
                        inputMode: 'numeric',
                      },
                    }}
                    inputRef={passcodeRef4}
                  />

                  <FormikTextField
                    name="passcode5"
                    hideErrors={true}
                    className="fs-exclude"
                    sx={classes.passcodeInput}
                    value={values.passcode5}
                    onPaste={(event) => onPaste(event as unknown as ClipboardEvent, setFieldValue)}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event, passcodeRef6, 'passcode5', values, setFieldValue)}
                    slotProps={{
                      htmlInput: {
                        'aria-label': 'code field 5',
                        inputMode: 'numeric',
                      },
                    }}
                    inputRef={passcodeRef5}
                  />

                  <FormikTextField
                    name="passcode6"
                    hideErrors={true}
                    className="fs-exclude"
                    sx={classes.passcodeInput}
                    value={values.passcode6}
                    onPaste={(event) => onPaste(event as unknown as ClipboardEvent, setFieldValue)}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event, undefined, 'passcode6', values, setFieldValue)}
                    slotProps={{
                      htmlInput: {
                        'aria-label': 'code field 6',
                        inputMode: 'numeric',
                      },
                    }}
                    inputRef={passcodeRef6}
                  />
                </Grid>

                <Grid container={true} size={12} sx={classes.centeredRow}>
                  {!submitError ? (
                    <>{dirty && !isValid && submitCount > 0 && <FormHelperText sx={sharedClasses.errorText}>{errors.passcode}</FormHelperText>}</>
                  ) : (
                    <FormHelperText sx={sharedClasses.errorText}>{submitError}</FormHelperText>
                  )}
                </Grid>

                <SubmitContainer>
                  <Button type="submit" loading={isSubmitting} disabled={values.passcode.length < 6} variant="contained" sx={sharedClasses.submitButton}>
                    Continue
                  </Button>

                  <Grid container={true} sx={classes.linksColumn}>
                    <Button loading={isResending} variant="text" color="primary" onClick={() => resendCode(resetForm)} sx={sharedClasses.linkButton}>
                      Resend code
                    </Button>

                    <Button variant="text" color="primary" onClick={() => navigate('/login', { state })} sx={sharedClasses.linkButton}>
                      Change email address
                    </Button>
                  </Grid>
                </SubmitContainer>
              </Grid>
            </Form>
          )}
        </Formik>
      </Grid>
    </Grid>
  )
}

export default EnterPasscode
